const PRINT_JOB_KEY_PREFIX = "print-job:";
const PROCESSING_TTL = 60_000;
const DONE_TTL = 86_400_000;
const PRINT_CLIENT_KEY = "print-client-id";

type PrintJobState = {
	expiresAt: number;
	owner: string;
	status: "done" | "processing";
};

const getStorage = () => {
	if (typeof window === "undefined") {
		return null;
	}

	return window.localStorage;
};

const getStorageKey = (jobKey: string) => {
	return `${PRINT_JOB_KEY_PREFIX}${jobKey}`;
};

export const getPrintClientId = () => {
	if (typeof window === "undefined") {
		return "server";
	}

	const storage = window.sessionStorage;
	const currentClientId = storage.getItem(PRINT_CLIENT_KEY);
	if (currentClientId) {
		return currentClientId;
	}

	const nextClientId = `${Date.now()}:${crypto.randomUUID()}`;
	storage.setItem(PRINT_CLIENT_KEY, nextClientId);

	return nextClientId;
};

const getState = (jobKey: string): PrintJobState | null => {
	const storage = getStorage();
	if (!storage) {
		return null;
	}

	const value = storage.getItem(getStorageKey(jobKey));
	if (!value) {
		return null;
	}

	try {
		return JSON.parse(value) as PrintJobState;
	} catch (err) {
		storage.removeItem(getStorageKey(jobKey));
		return null;
	}
};

const setState = (jobKey: string, state: PrintJobState) => {
	const storage = getStorage();
	if (!storage) {
		return;
	}

	storage.setItem(getStorageKey(jobKey), JSON.stringify(state));
};

const isActive = (state: PrintJobState, now: number) => {
	return state.expiresAt > now;
};

export const takePrintJob = (jobKey: string, now: number = Date.now()) => {
	const storage = getStorage();
	if (!storage) {
		return true;
	}

	const currentState = getState(jobKey);
	if (currentState && isActive(currentState, now)) {
		return false;
	}

	const nextState = {
		expiresAt: now + PROCESSING_TTL,
		owner: `${now}:${Math.random()}`,
		status: "processing" as const,
	};
	setState(jobKey, nextState);

	return getState(jobKey)?.owner === nextState.owner;
};

export const completePrintJob = (
	jobKey: string,
	now: number = Date.now(),
) => {
	setState(jobKey, {
		expiresAt: now + DONE_TTL,
		owner: `${now}:done`,
		status: "done",
	});
};

export const releasePrintJob = (jobKey: string) => {
	const storage = getStorage();
	if (!storage) {
		return;
	}

	const currentState = getState(jobKey);
	if (currentState?.status !== "processing") {
		return;
	}

	storage.removeItem(getStorageKey(jobKey));
};
